import _cloneDeep from "lodash/cloneDeep";
import _get from "lodash/get";
import { isSkuInCart } from "./cartSelectors";
import { getCheckout, State as CheckoutState } from "./checkout";
import { getPhoneNumberCheckType } from "./phoneNumberSelectors";
import { getLegalSubscriptionId, isReSwitch } from "./subscriptionsSelectors";
import { isLegalOwnerConsentRequired, isLegalOwnerVerified } from "./verificationSelectors";
import { CUSTOMER_TYPE, DELIVERY_TYPE, DeliveryType, MaybeEmpty } from "../../../../../constants";
import { DeliveryMethod } from "../../../../../models";
import {
    DeliveryAddress,
    DeliveryInfoContactInfo,
    DeliveryInfoSelectedState,
    DeliveryInfoState,
    PickupPoint,
    ShopDetails,
} from "../../../../../models/state";
import { getPickupPointPoints, State as PickupPointState } from "../../../pickup-points/state/selectors";
import { shopPickerUtils } from "../../../shop-picker";
import { getShops, State as ShopPickerState } from "../../../shop-picker/state/selectors";

export type State = CheckoutState & ShopPickerState & PickupPointState;
export const getDeliveryInfo: (state: State) => MaybeEmpty<DeliveryInfoState> = (state) =>
    getCheckout(state).deliveryInfo || {};
export const getDeliveryInfoAddress = (state: State): Partial<DeliveryAddress> =>
    getDeliveryInfo(state).deliveryAddress || {};
export const getDeliveryInfoContactInfo: (state: State) => Partial<DeliveryInfoContactInfo> = (state) =>
    getDeliveryInfo(state).contactInfo || {};
export const getContactInfoEmail: (state: State) => string = (state) => getDeliveryInfoContactInfo(state).email || "";
export const getContactInfoPhone: (state: State) => string = (state) =>
    getDeliveryInfoContactInfo(state).phoneNumber || "";
export const getDeliveryInfoMethods: (state: State) => DeliveryMethod[] = (state) => {
    const methods = _cloneDeep(getDeliveryInfo(state).methods || []);

    // Galaxy Fold event (skus: 9072960, 9072961) - only show K&H
    // TODO: Remove this whole commit when fold campaign is over
    if (isSkuInCart(state, "9072961") || isSkuInCart(state, "9072960")) {
        return methods.filter((method) => method.type === DELIVERY_TYPE.PICK_UP_IN_SHOP);
    }

    return methods;
};
export const getDeliveryInfoSelected: (state: State) => Partial<DeliveryInfoSelectedState> = (state) =>
    getDeliveryInfo(state).selected || {};
export const getDeliveryInfoType: (state: State) => DeliveryType | undefined = (state) =>
    getDeliveryInfoSelected(state).type;
export const getDeliveryInfoCode: (state: State) => string | undefined = (state) => getDeliveryInfoSelected(state).code;
export const getDeliveryInfoZipCode: (state: State) => string | undefined = (state) =>
    getDeliveryInfoSelected(state).zipCode;
export const getDeliveryInfoNote: (state: State) => string | undefined = (state) => getDeliveryInfo(state).note;
export const getDeliveryInfoEstimatedDate: (state: State) => string = (state) =>
    getDeliveryInfoSelected(state).estimatedDeliveryDate || "";
export const getEnhancedDeliveryInfoSelected: (state: State) => DeliveryMethod | undefined = (state) => {
    const selectedType = getDeliveryInfoType(state);
    return getDeliveryInfoMethods(state).find((m) => m.type === selectedType);
};
export const getDeliveryInfoSelectedPrice: (state: State) => number = (state) =>
    _get(getEnhancedDeliveryInfoSelected(state), "price", 0);

export const getSelectedShopInfo: (state: State) => ShopDetails | undefined = (state) => {
    const deliveryCode = getDeliveryInfoCode(state);
    const shopAvailability = shopPickerUtils.flattenShopAvailability(getShops(state));
    return shopAvailability.find((shop) => shop.code === deliveryCode);
};
export const getSelectedPickupPointInfo: (state: State) => PickupPoint | null = (state) => {
    const deliveryCode = getDeliveryInfoCode(state);
    const points = getPickupPointPoints(state);
    return points.find((point) => point.code === deliveryCode) || null;
};

export const getDeliveryDescription: (state: State) => string = (state) => {
    const deliveryType = getDeliveryInfoType(state);
    let description = getDeliveryInfoSelected(state).description || "";

    // For PICK_UP_IN_SHOP we only show the chosen shop name
    if (deliveryType === DELIVERY_TYPE.PICK_UP_IN_SHOP) {
        const shopName = getSelectedShopInfo(state)?.name || getDeliveryInfoSelected(state).shopName;
        if (shopName) {
            description = shopName;
        }
        // For PICK_UP_POINT we only show the chosen pickup point's name
    } else if (deliveryType === DELIVERY_TYPE.PICK_UP_POINT) {
        const point = getSelectedPickupPointInfo(state);
        if (point) {
            description = point.name;
        }
    }

    return description;
};
export const isDeliveryInfoReadOnly: (state: State) => boolean = (state) => {
    // TODO: Refactor to not rely on legalSubscriptionId
    // * isReSwitch => there exists one subscription with ReSwitch (?)
    // * getPhoneNumberCheckType => maybe use CUSTOMER_INFO_SELECTORS.getCurrentType() (?)
    const legalSubscriptionId = getLegalSubscriptionId(state);
    if (isReSwitch(state, legalSubscriptionId)) return false;
    if (getPhoneNumberCheckType(state, legalSubscriptionId) === CUSTOMER_TYPE.EXISTING && isLegalOwnerVerified(state))
        return false;
    return !!(
        getPhoneNumberCheckType(state, legalSubscriptionId) === CUSTOMER_TYPE.EXISTING &&
        isLegalOwnerConsentRequired(state)
    );
};

const getOnboardingMobileSupport = (state: State) => getDeliveryInfo(state).onboardingMobileSupport;

export const getOnboardingMobileSupportInfo = (state: State) => {
    return getOnboardingMobileSupport(state)?.info;
};

export const getSelectedOnboardingSku = (state: State) => {
    const onboardingMobileSupport = getOnboardingMobileSupport(state);
    if (onboardingMobileSupport?.selectedSku && onboardingMobileSupport?.selectedSku !== "") {
        return onboardingMobileSupport?.selectedSku;
    }
    return "";
};
